<template>
    <template v-if="!loaded">
        <div class="wrapper loading-skeleton">
            <div class="inner-wrapper">
                <div class="image-container" v-if="showImage">
                    <div class="placeholder-image"></div>
                </div>

                <section class="content-container">
                    <div class="content">
                        <div role="status" class="motion-safe:rd-pulse-slow rd-space-y-3">
                            <div class="rd-h-5 bg-neutral-4 rd-rounded-full rd-max-w-lg"></div>

                            <section class="rd-flex rd-items-center rd-w-full rd-mt-4">
                                <span class="rd-h-3.5 bg-neutral-6 rd-rounded-full rd-w-2/6"></span>
                                <span class="rd-h-3.5 rd-ms-2.5 bg-neutral-6 rd-rounded-full rd-w-1/6"></span>
                                <span class="rd-h-3.5 rd-ms-2.5 bg-neutral-6 rd-rounded-full rd-w-3/6"></span>
                            </section>

                            <section class="rd-flex rd-items-center rd-w-full rd-mt-1.5">
                                <span class="rd-h-3.5 bg-neutral-6 rd-rounded-full rd-w-3/6"></span>
                                <span class="rd-h-3.5 bg-neutral-6 rd-rounded-full rd-ms-2.5 rd-w-2/6"></span>
                                <span class="rd-h-3.5 rd-ms-2.5 bg-neutral-6 rd-rounded-full rd-w-1/6"></span>
                            </section>

                            <section class="rd-flex rd-items-center rd-w-full rd-mt-1.5">
                                <span class="rd-h-3.5 bg-neutral-6 rd-rounded-full rd-w-2/6"></span>
                                <span class="rd-h-3.5 rd-ms-2.5 bg-neutral-6 rd-rounded-full rd-w-1/6"></span>
                                <span class="rd-h-3.5 rd-ms-2.5 bg-neutral-6 rd-rounded-full rd-w-3/6"></span>
                            </section>

                            <section class="rd-flex rd-items-center rd-w-full rd-mt-1.5">
                                <span class="rd-h-3.5 bg-neutral-6 rd-rounded-full rd-w-3/6"></span>
                                <span class="rd-h-3.5 bg-neutral-6 rd-rounded-full rd-ms-2.5 rd-w-2/6"></span>
                                <span class="rd-h-3.5 rd-ms-2.5 bg-neutral-6 rd-rounded-full rd-w-1/6"></span>
                            </section>

                            <span class="rd-sr-only">Loading...</span>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </template>

    <template v-else>
        <div class="wrapper" :class="{ 'active': renderStore.isProjectActive(id) }">

            <div class="inner-wrapper">
                <Transition name="fade-in-out">
                    <button v-if="canRemove" type="button" @click.stop="remove" class="rd-btn rd-btn-remove">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                            aria-hidden="true">
                            <path
                                d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 4V6H15V4H9Z">
                            </path>
                        </svg>

                        <span class="rd-sr-only">Remove project selection</span>
                    </button>
                </Transition>

                <div class="image-container" v-if="showImage">

                    <img v-if="imageUrl" :src="imageUrl" class="rd-img-fluid" :alt="`Image for ${name} project`" />

                    <div v-else class="placeholder-image rd-img-fluid"></div>
                </div>

                <div class="content-container">
                    <div class="content">
                        <span class="title" :class="{ 'rd-w-4/5': canRemove }">{{ name }}</span>

                        <div v-if="hasDescription" class="text" v-html="description"></div>
                    </div>

                    <slot name="projectGiftArray"></slot>
                </div>
            </div>
        </div>
    </template>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { PartialDisplayType } from "@/builder/enums";
import { useRenderStore } from "@/builder/stores/renderStore";
import { publicApiService } from "@/services/publicApiService";

export default defineComponent({
    name: "ProjectCardComponent",
    props: ["id", "showImage", "showDescription"],
    setup() {
        const renderStore = useRenderStore();
        const publicApi = inject('publicApiService') as publicApiService;
        return { renderStore, publicApi };
    },
    data() {
        return {
            name: '',
            imageUrl: '' as string | null,
            description: '' as string | null,
            code: '' as string | null,
            loaded: false
        }
    },
    computed: {
        canRemove() {
            if (this.renderStore.usingPayPal) {
                return false;
            }

            //is this project in the store project splits
            const projectSplitIndex = this.renderStore.gift.projectSplits.findIndex(
                (x) => x.projectId === this.id
            );
            return projectSplitIndex > -1;
        },

        hasDescription() {
            return this.showDescription && this.description !== '';
        },

        isModalDisplay() {
            return this.renderStore.page.partialDisplayType === PartialDisplayType.Modal;
        }
    },
    async mounted() {
        const response = await this.publicApi.getProject(this.id, this.renderStore.organizationId);
        this.name = response.name;
        this.imageUrl = response.imageUrl;
        this.description = response.description;
        this.code = response.code;
        this.loaded = true;
    },
    methods: {
        remove(): void {
            this.$emit("remove-project", this.id);
        }
    },
});
</script>
